import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { submitEmailOnly, verifyPreLanderOtp } from '../../utils/api';

const OTP_LENGTH = 5;
const RESEND_COOLDOWN_FALLBACK = 60;

const getResendSeconds = (otpExpiry) => {
  if (!otpExpiry) return RESEND_COOLDOWN_FALLBACK;
  const ms = new Date(otpExpiry).getTime() - Date.now();
  return ms > 0 ? Math.ceil(ms / 1000) : 0;
};

const OtpStep = ({
  email,
  selections,
  ab_testing,
  initialOtpExpiry,
  onBack,
  onVerified,
  variant = 'login',
}) => {
  const { t } = useTranslation();
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [resendIn, setResendIn] = useState(() => getResendSeconds(initialOtpExpiry));
  const inputsRef = useRef([]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setInterval(() => {
      setResendIn((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    setOtp((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    if (error) setError('');
    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    setOtp(() => {
      const next = Array(OTP_LENGTH).fill('');
      for (let i = 0; i < OTP_LENGTH; i += 1) {
        next[i] = pasted[i] || '';
      }
      return next;
    });
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
    if (error) setError('');
  };

  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    const code = otp.join('');
    if (code.length !== OTP_LENGTH) {
      setError(t('enter_5code'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await verifyPreLanderOtp(email, code, { selections, ab_testing });
      if (data?.success === false) {
        setError(data.message || t('please_check_email'));
        return;
      }
      if (data?.authToken || data?.token) {
        onVerified?.(data);
        return;
      }
      setError(data?.message || t('please_check_email'));
    } catch (err) {
      setError(err?.message || t('network_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true);
    setError('');
    try {
      const data = await submitEmailOnly(email, {
        isResend: true,
        selections,
        ab_testing,
      });
      if (data?.success === false) {
        setError(data.message || t('network_error'));
        return;
      }
      if (data?.redirectUrl) {
        onVerified?.(data);
        return;
      }
      setResendIn(getResendSeconds(data?.otpExpiry) || RESEND_COOLDOWN_FALLBACK);
    } catch (err) {
      setError(err?.message || t('network_error'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className='account-container'>
      <div className='account-form-wrapper'>
        <div className='form-header'>
          <h2 className='account-title'>{t('verify_code')}</h2>
        </div>

        <form className='account-form' onSubmit={handleVerify}>
          <div
            style={{
              textAlign: 'center',
              color: '#fff',
              fontFamily: 'Poppins',
              fontWeight: 500,
              lineHeight: '140%',
              marginBottom: 8,
            }}
          >
            <div style={{ fontSize: 14, opacity: 0.75 }}>{t('enter_code')}</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4, wordBreak: 'break-all', textTransform: 'none' }}>
              {email}
            </div>
          </div>

          <div className='otp-inputs'>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputsRef.current[index] = el; }}
                className='verifyCodeInput'
                type='text'
                inputMode='numeric'
                pattern='\d*'
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                aria-label={`Code digit ${index + 1}`}
              />
            ))}
          </div>

          {error && <div className='error-message'>{error}</div>}

          <button type='submit' className='submit-button' disabled={loading}>
            {loading ? t('verifying') : t('apply_code')}
          </button>

          <div className='login-link'>
            <button
              type='button'
              className='link-button'
              onClick={handleResend}
              disabled={resendIn > 0 || resending}
            >
              {resendIn > 0
                ? `${t('resend_code')} (${resendIn}s)`
                : resending
                  ? t('sending')
                  : t('resend_code')}
            </button>
          </div>

          <div className='login-link'>
            <button type='button' className='link-button' onClick={onBack}>
              ← Back
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OtpStep;
